import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { ViewModelIndividualPermissionLocales } from './types';
import type {
  GroupMembers,
  ViewModelIndividualPermissionRole,
  ViewModelIndividualPermissions,
  WorkspaceMembers,
} from './types';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '@framework/ui/atoms/Button';
import { ViewModelOperation, type ViewModelEntity } from '@view-model/domain/view-model';
import { classNames, generateDefaultUserIcon } from '@framework/utils';
import type { UserId } from 'balus-common';
import { difference } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import type { UserPublicProfile } from '@user/PublicProfile';
import { toast } from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose(): void;
  viewModel: ViewModelEntity;
  groupMembers: GroupMembers;
  workspaceMembers: WorkspaceMembers;
  individualPermissions: ViewModelIndividualPermissions;
};

export const MemberAddModal: React.FC<Props> = ({
  isOpen,
  onClose,
  viewModel,
  groupMembers,
  workspaceMembers,
  individualPermissions,
}: Props) => {
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<UserId[]>([]);
  const [role, setRole] = useState<ViewModelIndividualPermissionRole>('editor');
  const [processing, setProcessing] = useState(false);
  const changed = selectedUserIds.length > 0;

  const availableUsers = useMemo(() => {
    const availableUserIds = new Set(
      difference(
        groupMembers.map(([profile]) => profile.id),
        workspaceMembers.map(([profile]) => profile.id),
        individualPermissions.map(([profile]) => profile.id)
      )
    );

    return groupMembers.filter(([profile]) => availableUserIds.has(profile.id));
  }, [groupMembers, individualPermissions, workspaceMembers]);

  const handleChangeSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setIsSelectedAll(checked);
      setSelectedUserIds(checked ? availableUsers.map(([profile]) => profile.id) : []);
    },
    [availableUsers]
  );

  const handleClose = useCallback(() => {
    setProcessing(false);
    setRole('editor');
    setIsSelectedAll(false);
    onClose();
  }, [onClose]);

  const handleChangeUser = useCallback(
    (userId: string, checked: boolean) => {
      const newSelectedUserIds = checked ? [...selectedUserIds, userId] : selectedUserIds.filter((id) => id !== userId);
      setSelectedUserIds(newSelectedUserIds);
      setIsSelectedAll(availableUsers.map(([profile]) => profile.id).every((id) => newSelectedUserIds.includes(id)));
    },
    [availableUsers, selectedUserIds]
  );

  const handleSubmit = useCallback(async () => {
    setProcessing(true);
    try {
      await ViewModelOperation.addIndividualPermissions(viewModel.id, selectedUserIds, role);
      toast.success('メンバーの追加に成功しました');
      handleClose();
    } catch (e) {
      toast.error('メンバーの追加に失敗しました');
      throw e;
    } finally {
      setProcessing(false);
    }
  }, [handleClose, role, selectedUserIds, viewModel.id]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`${viewModel.name.toString()}にメンバーを追加する`}
      submitButton={
        <Button color="primary" onClick={handleSubmit} disabled={!changed || processing} loading={processing}>
          追加
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={handleClose} loading={processing}>
          キャンセル
        </Button>
      }
      containerClassName="overflow-scroll"
      containerStyle={{ maxHeight: '50vh' }}
    >
      <div className="mb-4 mt-2 p-2" style={{ width: '600px' }}>
        <label className="mb-5 flex grow cursor-pointer flex-row px-5">
          <input
            type="checkbox"
            className={classNames(
              'form-checkbox mr-1 h-6 w-6 border-brand',
              processing ? 'cursor-not-allowed bg-gray-300' : 'cursor-pointer'
            )}
            checked={isSelectedAll}
            onChange={handleChangeSelectAll}
            disabled={processing}
          />
          全員を選択/選択解除
        </label>

        <div className="relative p-2">
          <div className="flex min-w-0 flex-1">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="min-w-0 flex-1 rounded-md border-2 border-gray-400 py-1 pr-2 placeholder:text-sm focus:outline-none"
              placeholder="検索"
              style={{ paddingInlineStart: '32px' }}
            />
          </div>
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute flex-none text-gray-600"
            style={{ top: '20px', left: '20px' }}
          />
        </div>

        {availableUsers.length === 0 ? (
          <div className="flex justify-center px-2 py-4">追加可能なメンバーが見つかりません。</div>
        ) : (
          <ul className="px-2">
            {availableUsers.map(([profile, role]) => {
              if (!profile.match(keyword)) return null;

              return (
                <MemberItem
                  key={profile.id}
                  profile={profile}
                  checked={selectedUserIds.includes(profile.id)}
                  isGuest={role === 'guest'}
                  onChange={handleChangeUser}
                />
              );
            })}
          </ul>
        )}

        {selectedUserIds.length > 0 ? (
          <div className="py-2 pl-10">
            上記で選択した{selectedUserIds.length}名を
            <select
              className="form-select w-24 py-1 text-sm"
              defaultValue={role}
              onChange={(e) => setRole(e.target.value as ViewModelIndividualPermissionRole)}
            >
              {Object.entries(ViewModelIndividualPermissionLocales).map(([role, label]) => (
                <option key={role} value={role}>
                  {label}
                </option>
              ))}
            </select>
            として追加します。
          </div>
        ) : (
          <div className="h-12 py-2"></div>
        )}
      </div>
    </GeneralModal>
  );
};

type ItemProps = {
  profile: UserPublicProfile;
  checked: boolean;
  isGuest: boolean;
  onChange(userId: string, checked: boolean): void;
};

const MemberItem: React.FC<ItemProps> = ({ profile, checked, isGuest, onChange }: ItemProps) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(profile.id, event.target.checked);
    },
    [onChange, profile.id]
  );

  return (
    <li className="flex flex-row items-center px-3 py-2 hover:bg-gray-200">
      <label className="flex grow cursor-pointer flex-row truncate">
        <input
          type="checkbox"
          className="form-checkbox m-1 size-6 border-brand"
          checked={checked}
          onChange={handleChange}
        />
        <span className="my-1 ml-1 flex size-6 overflow-hidden rounded-full">
          {profile.imageUrl ? (
            <img className="size-full object-cover" src={profile.imageUrl} alt={profile.name} />
          ) : (
            <div className="size-full object-cover">
              {generateDefaultUserIcon({
                id: profile.id,
                name: profile.name,
                size: 24,
              })}
            </div>
          )}
        </span>
        <span className="my-1 flex h-6 grow truncate px-2 leading-6">{profile.name}</span>
        {isGuest && <span className="m-1 rounded-lg bg-gray-400 px-2 text-xs leading-6">ゲスト</span>}
      </label>
    </li>
  );
};
